import { useCallback, useEffect, useMemo } from "react";

import {
  AddBusinessOutlined,
  ApartmentOutlined,
  CurrencyExchangeOutlined,
  FeedOutlined,
  FolderOpenOutlined,
  GavelOutlined,
  HourglassBottom,
  HourglassEmptyOutlined,
  HourglassFullOutlined,
  HouseOutlined,
  LocalGroceryStoreOutlined,
  LogoutOutlined,
  PersonOutline,
} from "@mui/icons-material";
import { CircularProgress, useMediaQuery } from "@mui/material";
import { useStytch } from "@stytch/react";
import { has, isArray, isBoolean, isEmpty, isNil, size } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../_src_shared/DashboardLayout";

import Flx from "../components/layout/Flx";
import DashboardSidebar from "../components/sidebars/DashboardSidebar/DashboardSidebar";
import { useEndpointHook } from "../hooks/useEndpointHook";
import DevToolsPanel from "../testing/Devtoolspanel";
import ProfileSetupWizard from "./ProfileSetupWizard";
import AgUiBorrowerAssistantChat from "../components/agui/AgUiBorrowerAssistantChat/AgUiBorrowerAssistantChat";
import StagedApplicationWizard from "./StagedApplicationWizard";

const AuthenticatedDashboard = ({ children }) => {
  const stytch = useStytch();
  const isMobile = useMediaQuery("(max-width:768px)");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getProfile, getBorrowerFinancingApplications } = useEndpointHook({});
  // const { getBorrowerProfile } = useBorrowerPortalHook();
  // const { getBorrowerProfile } = useBorrowerPortalHook();

  // Redux state
  const profile = useSelector((state) => state.profile);
  const financingRequests = useSelector((state) => state.financingRequests);
  const navSidebarOpen = useSelector((state) => state?.navSidebar?.open);

  const open = useMemo(
    () => navSidebarOpen || isMobile,
    [navSidebarOpen, isMobile],
  );

  const handleLogoutUser = useCallback(async () => {
    await stytch.session.revoke();
    navigate("/");
  }, [stytch, navigate]);

  useEffect(() => {
    if (isNil(profile)) {
      console.log("Root - Fetching profile...");
      getProfile({});
    }
  }, [profile]);

  useEffect(() => {
    if (isNil(financingRequests)) {
      console.log("Root -Fetching financing requests...");
      getBorrowerFinancingApplications({});
    }
  }, [financingRequests]);
  // useEffect(() => {
  //   if (isNil(profile)) {
  //     console.log("Root - Fetching profile...");
  //     getProfile();
  //   }
  //   if (isNil(financingRequests) && !isNil(profile)) {
  //     console.log("Root -Fetching financing requests...");
  //     getBorrowerFinancingApplications();
  //   }
  // }, [profile]);

  return (
    <DashboardWrapper onLogoutClick={handleLogoutUser} open={open}>
      {process.env.NODE_ENV === "development" && <DevToolsPanel />}
      <ContentRenderer>
        {
          <>
            {children}
            <DashboardSidebar />
            {/* <AgUiBorrowerAssistantChat /> */}
            {/* <BorrowerAssistantChatSimple /> */}

            {/* <CopilotKitProvider>
              {children}
              <DashboardSidebar />
              <BorrowerAssistantCopilot />
            </CopilotKitProvider> */}
          </>
        }
      </ContentRenderer>
    </DashboardWrapper>
  );
};

const ContentRenderer = ({ children }) => {
  const profile = useSelector((state) => state.profile);
  const financingRequests = useSelector((state) => state.financingRequests);

  const hasFinancingRequests = useMemo(() => {
    return isArray(financingRequests) && !isEmpty(financingRequests);
  }, [financingRequests]);

  const hasStagedApplications = useMemo(() => {
    return (
      isArray(profile?.stagedApplications) &&
      !isEmpty(profile?.stagedApplications)
    );
  }, [profile?.stagedApplications]);

  const showProfileSetupWizard = useMemo(() => {
    // Trigger setup modal
    if (profile?.middleName === "test") {
      return false;
    }

    // Trigger setup modal
    if (
      isNil(profile?.firstName) ||
      isNil(profile?.lastName) ||
      isNil(profile?.dateOfBirth)
    ) {
      return false;
    }

    // Skip setup modal and show content
    if (isBoolean(profile?.profileComplete) && profile?.profileComplete) {
      return true;
    }
    return false;
  }, [profile]);

  // return <ProfileSetupWizard profile={profile} />;
  // return <StagedApplicationWizard show />;
  if (isNil(profile) || isNil(financingRequests)) {
    return (
      <Flx fw sx={{ height: "100vh" }} center>
        <CircularProgress />
      </Flx>
    );
  }

  // Show staged app wizard
  if (hasStagedApplications && !hasFinancingRequests) {
    return (
      <StagedApplicationWizard
        show
        stagedApplication={
          size(profile?.stagedApplications) === 1
            ? profile?.stagedApplications[0]
            : {}
        }
      />
    );
  }

  // Show normal dashboard content
  if (!showProfileSetupWizard || hasFinancingRequests) {
    return children;
  }

  // Show profile setup wizard
  return <ProfileSetupWizard profile={profile} />;
};

const DashboardWrapper = ({ open, onLogoutClick, children }) => {
  const topLinks = useMemo(() => {
    return [
      {
        items: [
          {
            icon: <HouseOutlined className="thin" />,
            to: "/",
            label: "Home",
          },

          {
            icon: <AddBusinessOutlined />,
            to: "/financing-request/create",
            label: "New Financing Request",
          },
          {
            icon: <HourglassEmptyOutlined />,
            to: "/staged-applications",
            label: "Staged Applications",
          },
          {
            icon: <FeedOutlined />,
            to: "/financing-requests",
            label: "In Progress Loans",
          },
          {
            icon: <GavelOutlined />,
            to: "/closed-loans",
            label: "Closed Loans",
          },
        ],
      },
    ];
  }, []);

  const bottomLinks = useMemo(
    () => [
      {
        items: [
          // {
          //   icon: <GroupOutlined className="thin" />,
          //   to: "/registered-users",
          //   label: "Registered Users",
          // },
          {
            icon: <PersonOutline className="thin" />,
            to: "/profile",
            label: "Profile",
          },
          {
            icon: <ApartmentOutlined className="thin" />,
            to: "/profile/entities",
            label: "Associated Entities & LLC's",
          },
          {
            icon: <FolderOpenOutlined className="thin" />,
            to: "/profile/files",
            label: "Document Library",
          },
          {
            icon: <PersonOutline className="thin" />,
            to: "/terms-of-use",
            label: "Terms of Use",
          },
          {
            type: "logout",
            label: "Log Out",
            onClick: onLogoutClick,
            icon: <LogoutOutlined sx={{ transform: "rotate(180deg)" }} />,
          },
        ],
      },
    ],
    [],
  );
  return (
    <DashboardLayout topLinks={topLinks} bottomLinks={bottomLinks} open={open}>
      {children}
    </DashboardLayout>
  );
};

/**
 * TEST DATA
 */

export default AuthenticatedDashboard;
